import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { OrderState, OrderType, Side } from '@protos/v2/order';
import { OrderNotification } from '@shared/contexts/OrderNotificationsContexts';
import { format } from 'date-fns';

const StyledListItem = styled(ListItem, {
  shouldForwardProp: prop => prop !== 'side',
})<{ side: Side }>(({ theme, side }) => ({
  borderLeft: `4px solid ${side === Side.BUY ? theme.palette.background.buy : theme.palette.background.sell}`,
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  '&.unread': {
    backgroundColor: theme.palette.action.hover,
    '& .MuiListItemText-root': {
      color: `${theme.palette.text.primary}`,
    },
  },
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiTypography-caption': {
    color: theme.palette.text.secondary,
  },
  '&.unread .MuiTypography-caption': {
    color: theme.palette.text.primary,
  },
}));

interface NotificationItemProps {
  notification: OrderNotification;
  onNotificationClick: (notification: OrderNotification) => void;
  onClearNotification: (id: string) => void;
  isDTUser?: boolean;
}

export const NotificationItem = ({ notification, onNotificationClick, onClearNotification, isDTUser }: NotificationItemProps) => {
  const symbolText =
    typeof notification.order.symbol === 'string'
      ? notification.order.symbol.toUpperCase()
      : `${notification.order.symbol.front.toUpperCase()}-${notification.order.symbol.back.toUpperCase()}`;
  const isQuotebookOrder = notification.order.order_type === OrderType.LIMIT_MAKER && notification.order.exchange === 'onyx';

  return (
    <StyledListItem
      secondaryAction={
        <Tooltip title="Clear notification">
          <IconButton onClick={() => onClearNotification(notification.id)} size="small" color="error" sx={{ mt: 8 }}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      }
      className={notification.read ? '' : 'unread'}
      onClick={() => onNotificationClick(notification)}
      side={notification.order.side}
    >
      <StyledListItemText
        primary={
          <>
            <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 5 }}>
              {format(notification.timestamp, 'MMM d, HH:mm:ss')}
            </Typography>
            <Typography variant="subtitle2">Order {notification.order.order_state === OrderState.FILLED ? 'Filled' : 'Partially Filled'}</Typography>
          </>
        }
        secondary={
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" color="text.secondary">
              {symbolText} : {isQuotebookOrder ? `${notification.order.executed_amount}/${notification.order.amount}` : notification.order.amount} @{' '}
              {notification.order.price}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {notification.order.side === Side.BUY ? 'BUY' : 'SELL'}
            </Typography>
            {isDTUser && (
              <>
                <Typography variant="caption" color="text.secondary">
                  {notification.order.user.full_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.order.user.account_name}
                </Typography>
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              {notification.order.account_id.toLocaleUpperCase('en-US')}
            </Typography>
          </Box>
        }
        className={notification.read ? '' : 'unread'}
      />
    </StyledListItem>
  );
};
